import {
  AfterViewInit,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { RequestService } from '../../../shared/services/request.service';
import { ComponentService } from '../../../shared/abstract/component.service';
import { ManagementType } from '../../../shared/enum/managementType';
import { ActionType } from '../../../shared/enum/actionType';
import { MatTableDataSource } from '@angular/material/table';
import { ModalComponent } from '../../../shared/widgets/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, map, Subject, Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import { IntegrationModel } from '../../../shared/model/integration-model';

interface SearchParams {
  searchTerm: string;
  columnName?: string;
}

@Component({
  selector: 'app-integration-management',
  templateUrl: './integration-management.component.html',
  styleUrl: './integration-management.component.css',
})
export class IntegrationManagementComponent
  extends ComponentService<any>
  implements OnInit, AfterViewInit, OnDestroy
{
  dataSource = new MatTableDataSource<any>();
  currentCompany;
  url;
  integrations: IntegrationModel[] = [];
  pagedIntegrations: IntegrationModel[] = [];
  filteredIntegrations: IntegrationModel[] = [];
  pageIndex = 0;
  pageSize = 5;
  currentPage = 0;
  totalPages = 0;
  maxVisibleButtons = 5;
  Math = Math;

  isSideNavOpen = false;
  selectedManagementType!: ManagementType;
  selectedActionType!: ActionType;
  selectedIntegration?: any;

  ManagementType = ManagementType;
  ActionType = ActionType;

  displayedColumns = ['value', 'options'];

  constructor(
    private _request: RequestService,
    injector: Injector,
    private _dialog: MatDialog
  ) {
    super(injector);
  }

  ngOnInit(): void {
    if (
      this._appsService.existCurrentProject?.companyId &&
      this._appsService.existCurrentProject?.projectId
    ) {
      this.currentProject = this._appsService.existCurrentProject;
      this.currentCompany = this._appsService.existCurrentCompany;
      this.managementConfig();
    }

    this.cPSubs = this._appsService.currentProject.subscribe((project) => {
      this.currentProject = project;
      this.managementConfig();
    });
    this.url = environment.apiUrl + '/';
  }

  ngOnDestroy(): void {
    this.cPSubs ? this.cPSubs.unsubscribe() : false;
  }

  ngAfterViewInit(): void {}

  managementConfig() {
    this.getIntegrations();
  }

  projectToggle(event: any) {
    if (event.checked) {
      this._appsService.projectCheckedIntegration = true;
    } else {
      this._appsService.projectCheckedIntegration = false;
    }
    this.getIntegrations();
  }

  getIntegrations() {
    if (this._appsService.projectCheckedIntegration == true) {
      this._request
        .getIntegrationSettings(this.currentCompany, this.currentProject.projectId)
        .then((res) => {
          if (res) {
            this.integrations = res;
          }
        })
        .catch((err) => {
          this.MessageBox(
            this.languageKey('unexpectedError'),
            'warn',
            'center'
          );
        })
        .finally(() => {
          this.updateTotalPages();
          this.currentPage = 0;
          this.pageIndex = 0;
          this.updatePagedData();
        });
    } else {
      this._request
        .getIntegrationSettings(this.currentCompany, null)
        .then((res) => {
          if (res) {
            this.integrations = res;
          }
        })
        .catch((err) => {
          this.MessageBox(
            this.languageKey('unexpectedError'),
            'warn',
            'center'
          );
        })
        .finally(() => {
          this.updateTotalPages();
          this.currentPage = 0;
          this.pageIndex = 0;
          this.updatePagedData();
        });
    }
  }

  // PAGINATE
  get visiblePageNumbers(): number[] {
    const start = Math.max(
      0,
      Math.min(
        this.currentPage - Math.floor(this.maxVisibleButtons / 2),
        this.totalPages - this.maxVisibleButtons
      )
    );
    return Array.from(
      { length: Math.min(this.maxVisibleButtons, this.totalPages) },
      (_, i) => start + i + 1
    );
  }

  onPageChange(pageIndex: number) {
    if (pageIndex >= 0 && pageIndex < this.totalPages) {
      this.currentPage = pageIndex;
    }
    this.pageIndex = pageIndex;
    this.updatePagedData();
  }

  updatePagedData(filteredIntegration: IntegrationModel[] | null = null) {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    if (filteredIntegration != null) {
      this.pagedIntegrations = this.filteredIntegrations.slice(
        startIndex,
        endIndex
      );
    } else {
      this.pagedIntegrations = this.integrations.slice(startIndex, endIndex);
    }
    this.dataSource = new MatTableDataSource(this.pagedIntegrations);
  }

  updatePageSize(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.pageSize = parseInt(selectElement.value, 10);
    this.updateTotalPages();
    this.currentPage = 0;
    this.pageIndex = 0;
    this.updatePagedData();
  }

  updateTotalPages() {
    this.totalPages = Math.ceil(this.integrations.length / this.pageSize);
  }

  deleteIntegration(element) {
    const dialogRef = this._dialog.open(ModalComponent, {
      width: '400px',
      data: {
        type: 'confirm',
        title: 'deleteIntegration',
        body: 'deleteIntegrationConfirm',
        ok: 'confirm',
        cancel: 'cancel',
        color: 'warn',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this._appsService.projectCheckedIntegration == true) {
          this._request
            .deleteIntegrationSetting(
              element.id,
              this.currentCompany,
              this._appsService.existCurrentProject.projectId
            )
            .then((res) => {
              if (res.code == 1) {
                this.dataSource.data = this.dataSource.data.filter(
                  (u) => u.id != element.id
                );
                this.MessageBox(res.message ?? '', 'primary', 'center');
              } else if (res.code == 2) {
                this.MessageBox(res.message ?? '', 'orange', 'center');
              } else {
                this.MessageBox(res.message ?? '', 'warn', 'center');
              }
            })
            .catch((err) => {
              this.MessageBox(
                this.langauge('unexpectedError'),
                'warn',
                'center'
              );
            })
            .finally(() => {
              this.getIntegrations();
            });
        } else {
          this._request
            .deleteIntegrationSetting(element.id, this.currentCompany, null)
            .then((res) => {
              if (res.code == 1) {
                this.dataSource.data = this.dataSource.data.filter(
                  (u) => u.id != element.id
                );
                this.MessageBox(res.message ?? '', 'primary', 'center');
              } else if (res.code == 2) {
                this.MessageBox(res.message ?? '', 'orange', 'center');
              } else {
                this.MessageBox(res.message ?? '', 'warn', 'center');
              }
            })
            .catch((err) => {
              this.MessageBox(
                this.languageKey('unexpectedError'),
                'warn',
                'center'
              );
            })
            .finally(() => {
              this.getIntegrations();
            });
        }
      }
    });
    this._appsService.logInfo("Entegrasyon silindi");
  }

  openSideNav(
    managementType: ManagementType,
    actionType: ActionType,
    integration?: any
  ) {
    this.selectedManagementType = managementType;
    this.selectedActionType = actionType;
    this.isSideNavOpen = true;
    this.selectedIntegration = integration ? { ...integration } : null;
    this._appsService.logInfo("Entegrasyon " + this.languageKey(actionType) + " açıldı");
  }
}
